/**
 * MVP 5.5 — platform admin console at `/admin` (role `admin` only).
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AdminUserRow } from './api/admin';
import {
  adminFlagsList,
  adminListUsers,
  adminSetDiscount,
  adminToggleBypass,
  adminUserAudit,
  putFlag,
} from './api/admin';
import { ApiError } from './api/client';
import { getMe } from './api/me';
import { useAuth } from './auth/AuthContext';
import styles from './AppV1.module.css';

type AdminTab = 'users' | 'audit' | 'flags';

export function AdminConsole(): JSX.Element | null {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [tab, setTab] = useState<AdminTab>('users');
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [flags, setFlags] = useState<Array<Record<string, unknown> & { name: string }>>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [auditEntries, setAuditEntries] = useState<unknown[]>([]);
  const [reason, setReason] = useState('');
  const [discountInput, setDiscountInput] = useState('');
  const [flagReason, setFlagReason] = useState('');
  const [selectedFlag, setSelectedFlag] = useState('');
  const [flagEnabled, setFlagEnabled] = useState<boolean | undefined>(undefined);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    getMe()
      .then((m) => {
        if (cancel) return;
        if (m.role !== 'admin') {
          navigate('/', { replace: true });
          return;
        }
        setAllowed(true);
      })
      .catch(() => navigate('/', { replace: true }));
    return () => {
      cancel = true;
    };
  }, [navigate]);

  useEffect(() => {
    if (!allowed) return;
    setErr(null);
    setMsg(null);
    if (tab === 'users') {
      adminListUsers()
        .then((r) => setUsers(r.users ?? []))
        .catch((e) => setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e)));
    } else if (tab === 'flags') {
      adminFlagsList()
        .then((r) => setFlags(r.flags ?? []))
        .catch((e) => setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e)));
    }
  }, [allowed, tab]);

  useEffect(() => {
    if (!allowed || tab !== 'audit' || !selectedUserId) {
      setAuditEntries([]);
      return;
    }
    adminUserAudit(selectedUserId)
      .then((r) => setAuditEntries(r.entries ?? []))
      .catch((e) => setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e)));
  }, [allowed, tab, selectedUserId]);

  if (allowed === null) {
    return (
      <main className={styles.page}>
        <p className={styles.subtitle}>Checking admin access…</p>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <section className={`${styles.card} ${styles.wideCard}`}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>Admin console</h1>
            <p className={styles.muted}>
              Bypass toggles may take up to ~5 minutes to affect every API call (API Gateway authorizer cache).
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Link to="/" className={styles.linkButton}>
              ← App home
            </Link>
            <button type="button" className={styles.linkButton} onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </header>

        <nav className={styles.nav} aria-label="Admin sections">
          {(['users', 'audit', 'flags'] as const).map((t) => (
            <button
              key={t}
              type="button"
              className={`${styles.navLink} ${tab === t ? styles.navActive : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'users' ? 'Users' : t === 'audit' ? 'Audit' : 'Flags'}
            </button>
          ))}
        </nav>

        {err ? (
          <p role="alert" className={styles.error}>
            {err}
          </p>
        ) : null}
        {msg ? <p className={styles.muted}>{msg}</p> : null}

        {tab === 'users' ? (
          <div className={styles.subCard}>
            <h2 className={styles.subTitle}>Users</h2>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>userId</th>
                  <th>Bypass</th>
                  <th>Discount %</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.userId}>
                    <td>{u.email}</td>
                    <td className={styles.value}>{u.userId}</td>
                    <td>{u.bypassBilling ? 'yes' : 'no'}</td>
                    <td>{u.discountPct === null ? '—' : String(u.discountPct)}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={() => {
                          setSelectedUserId(u.userId);
                          setTab('audit');
                        }}
                      >
                        Audit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h3 className={styles.subTitle}>Toggle bypass</h3>
            <div className={styles.form}>
              <label className={styles.label} htmlFor="uid-bypass">
                User id (Cognito sub)
              </label>
              <input
                id="uid-bypass"
                className={styles.input}
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                placeholder="paste sub"
              />
              <label className={styles.label} htmlFor="reason-bypass">
                Reason (≥10 chars)
              </label>
              <textarea
                id="reason-bypass"
                className={styles.input}
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => {
                  setErr(null);
                  setMsg(null);
                  adminToggleBypass(selectedUserId.trim(), reason.trim())
                    .then(() => {
                      setMsg('Bypass toggled.');
                      return adminListUsers();
                    })
                    .then((r) => setUsers(r.users ?? []))
                    .catch((e) =>
                      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e)),
                    );
                }}
              >
                POST override-bypass (toggle)
              </button>
            </div>
            <h3 className={styles.subTitle}>Set discount % (persist only; Stripe in MVP 6.5)</h3>
            <div className={styles.form}>
              <label className={styles.label} htmlFor="disc">
                Percent 0–100, or empty + reason to clear
              </label>
              <input
                id="disc"
                className={styles.input}
                value={discountInput}
                onChange={(e) => setDiscountInput(e.target.value)}
                placeholder="e.g. 25"
              />
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => {
                  setErr(null);
                  setMsg(null);
                  const raw = discountInput.trim();
                  const pct = raw === '' ? null : Number(raw);
                  if (raw !== '' && Number.isNaN(pct)) {
                    setErr('Invalid number');
                    return;
                  }
                  adminSetDiscount(selectedUserId.trim(), pct, reason.trim() || 'no reason given')
                    .then(() => {
                      setMsg('Discount updated.');
                      return adminListUsers();
                    })
                    .then((r) => setUsers(r.users ?? []))
                    .catch((e) =>
                      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e)),
                    );
                }}
              >
                POST override-discount
              </button>
            </div>
          </div>
        ) : null}

        {tab === 'audit' ? (
          <div className={styles.subCard}>
            <h2 className={styles.subTitle}>Audit log for user</h2>
            <label className={styles.label} htmlFor="audit-uid">
              Target user id
            </label>
            <input
              id="audit-uid"
              className={styles.input}
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            />
            <pre className={styles.code} style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {auditEntries.length ? JSON.stringify(auditEntries, null, 2) : 'No entries yet.'}
            </pre>
          </div>
        ) : null}

        {tab === 'flags' ? (
          <div className={styles.subCard}>
            <h2 className={styles.subTitle}>Flags</h2>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>name</th>
                  <th>enabled</th>
                  <th>effective (you)</th>
                  <th>mvp</th>
                </tr>
              </thead>
              <tbody>
                {flags.map((f) => (
                  <tr key={f.name}>
                    <td className={styles.value}>{f.name}</td>
                    <td>{String(f.enabled)}</td>
                    <td>{String(f.effectiveForYou)}</td>
                    <td>{String(f.owner_mvp ?? '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h3 className={styles.subTitle}>PUT flag (admin / tenant_admin)</h3>
            <div className={styles.form}>
              <input
                className={styles.input}
                placeholder="flag name e.g. f_meta_smoke"
                value={selectedFlag}
                onChange={(e) => setSelectedFlag(e.target.value)}
              />
              <label className={styles.label}>
                <input
                  type="checkbox"
                  checked={flagEnabled === true}
                  onChange={() => setFlagEnabled(flagEnabled === true ? undefined : true)}
                />{' '}
                Set enabled=true (uncheck to omit)
              </label>
              <textarea
                className={styles.input}
                rows={2}
                placeholder="reason ≥10 chars"
                value={flagReason}
                onChange={(e) => setFlagReason(e.target.value)}
              />
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => {
                  setErr(null);
                  setMsg(null);
                  const patch: {
                    reason: string;
                    enabled?: boolean;
                  } = { reason: flagReason.trim() };
                  if (flagEnabled === true) patch.enabled = true;
                  putFlag(selectedFlag.trim(), patch)
                    .then(() => {
                      setMsg('Flag updated.');
                      return adminFlagsList();
                    })
                    .then((r) => setFlags(r.flags ?? []))
                    .catch((e) =>
                      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e)),
                    );
                }}
              >
                PUT /flags/…
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </main>
  );
}
